import { useNavigate } from "react-router-dom";
import { Play, Pause, Rewind, FastForward, Volume2, VolumeX, X, Music } from "lucide-react";
import { useAudio } from "../contexts/AudioContext";
import { BACKEND_URL, COVER_IMAGE } from "../lib/constants";

const AudioPlayerBar = () => {
  const navigate = useNavigate();
  const {
    currentTrack, isPlaying, progress, duration, volume, playbackRate,
    autoPlayNext, togglePlay, seek, skipBy, changeVolume, cyclePlaybackRate, stop,
  } = useAudio();

  if (!currentTrack) return null;

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const onSeekClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    seek(pct * (duration || 0));
  };

  const cover = currentTrack.thumbnail_url
    ? (currentTrack.thumbnail_url.startsWith("http") ? currentTrack.thumbnail_url : `${BACKEND_URL}${currentTrack.thumbnail_url}`)
    : COVER_IMAGE;

  return (
    <div className="audio-player-bar glass-dirty" data-testid="audio-player-bar">
      {/* Top progress strip — full width */}
      <div
        className="h-1 bg-white/5 cursor-pointer relative group"
        onClick={onSeekClick}
        data-testid="player-bar-seek"
      >
        <div className="h-full bg-red-600 transition-all" style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }} />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5">
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Cover + title — clickable to nav to episode */}
          <button
            onClick={() => currentTrack.route && navigate(currentTrack.route)}
            className="flex items-center gap-3 min-w-0 flex-1 hover:opacity-90 transition-opacity"
            data-testid="player-bar-track-info"
          >
            <div
              className="w-10 h-10 sm:w-12 sm:h-12 rounded bg-cover bg-center flex-shrink-0 ring-1 ring-white/10 shadow-md"
              style={{ backgroundImage: `url(${cover})` }}
            >
              {!currentTrack.thumbnail_url && (
                <div className="w-full h-full bg-red-600/20 flex items-center justify-center rounded">
                  <Music size={16} className="text-red-500" />
                </div>
              )}
            </div>
            <div className="min-w-0 text-left">
              <p className="font-anton text-xs sm:text-sm truncate text-white">{currentTrack.title}</p>
              <p className="text-[10px] sm:text-xs text-gray-500 truncate">
                {currentTrack.subtitle || currentTrack.artist || "Red Ink Studios"}
                {autoPlayNext && <span className="ml-2 text-red-400 text-[9px] font-mono">AUTO</span>}
              </p>
            </div>
          </button>

          {/* Controls */}
          <div className="flex items-center gap-1.5 sm:gap-3">
            <button onClick={() => skipBy(-10)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors" title="Back 10s" data-testid="player-bar-skip-back">
              <Rewind size={15} />
            </button>
            <button
              onClick={togglePlay}
              className="p-2.5 bg-red-600 rounded-full hover:bg-red-700 transition-colors shadow-lg shadow-red-900/40"
              data-testid="player-bar-play-pause"
            >
              {isPlaying ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
            </button>
            <button onClick={() => skipBy(10)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors" title="Forward 10s" data-testid="player-bar-skip-fwd">
              <FastForward size={15} />
            </button>
          </div>

          {/* Time */}
          <div className="hidden sm:flex items-center gap-1 text-[10px] text-gray-500 font-mono whitespace-nowrap">
            <span>{formatTime(progress)}</span>
            <span className="opacity-50">/</span>
            <span>{formatTime(duration)}</span>
          </div>

          {/* Speed (desktop only) */}
          <button
            onClick={cyclePlaybackRate}
            className={`hidden md:flex items-center justify-center min-w-[2.5rem] px-2 py-1 rounded text-[10px] font-mono transition-colors ${playbackRate !== 1 ? "bg-red-600/20 text-red-400 border border-red-500/30" : "hover:bg-white/10 text-gray-300 border border-white/5"}`}
            title="Click to cycle speed"
            data-testid="player-bar-speed"
          >
            {playbackRate}x
          </button>

          {/* Volume (desktop only) */}
          <div className="hidden md:flex items-center gap-1.5">
            <button onClick={() => changeVolume(volume === 0 ? 1 : 0)} className="p-1.5 hover:bg-white/10 rounded transition-colors" data-testid="player-bar-mute">
              {volume === 0 ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume}
              onChange={(e) => changeVolume(parseFloat(e.target.value))}
              className="w-16 accent-red-600"
              data-testid="player-bar-volume"
            />
          </div>

          {/* Close */}
          <button onClick={stop} className="p-1.5 hover:bg-white/10 rounded-full text-gray-500 hover:text-white transition-colors" title="Close player" data-testid="player-bar-close">
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayerBar;
